import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { forkJoin, finalize } from 'rxjs';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { EditorModule } from 'primeng/editor';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { DialogService } from 'primeng/dynamicdialog';
import { PartyService } from '../../../party/party.service';
import { QuickContactDialogComponent } from '../quick-contact-dialog/quick-contact-dialog.component';
import { ToolbarService } from '../../../services/toolbar.service';
import { Customer } from '../../../customer/customer.model';
import { CustomerService } from '../../../customer/customer.service';
import { DeliveryRateCard } from '../../../contracts/models/delivery-rate-card.model';
import { VehicleCapacity } from '../../../fleet/vehicle/params/vehicle-capacity';
import { VehicleConfig } from '../../../fleet/vehicle/params/vehicle-config';
import { VehicleType } from '../../../fleet/vehicle/params/vehicle-type';
import { City } from '../../../models/common/city.model';
import { Country } from '../../../models/common/country.model';
import { BaseMasterDataService } from '../../../services/base-master-data.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { UOM } from '../../../models/meta-params/uom';
import { OrdersService } from '../../../order/order.service';
import { PaymentTerm } from '../../../order/models/payment-term.model';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { WarehousingMasterData } from '../../../warehousing/warehousing.model';
import { WarehousingService } from '../../../warehousing/warehousing.service';
import { CrmClient, CrmLead, CrmProspect, CrmQuotation, CrmQuotationJobType, CrmQuotationStatus, CrmQuotationTermsTemplate, CrmQuotationType } from '../../crm.model';
import { CrmService } from '../../crm.service';

type SourceType = 'CLIENT' | 'PROSPECT' | 'LEAD';

interface SourceOption {
  label: string;
  value: string;
  type: SourceType;
  id?: number;
  record?: CrmClient | CrmProspect | CrmLead;
}

@Component({
  selector: 'app-quotation-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    DatePickerModule,
    DialogModule,
    EditorModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    ToggleSwitchModule,
    PageShellComponent,
    PageDetailHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    PageFormSectionComponent
  ],
  providers: [BaseMasterDataService, MessageService, DialogService],
  templateUrl: './quotation-form.component.html',
  styleUrls: ['./quotation-form.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuotationFormComponent implements OnInit {
  readonly CrmQuotationJobType = CrmQuotationJobType;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private crmService = inject(CrmService);
  private toolbarService = inject(ToolbarService);
  private partyService = inject(PartyService);
  private warehousingService = inject(WarehousingService);
  private customerService = inject(CustomerService);
  private masterDataService = inject(BaseMasterDataService);
  private sessionStorageService = inject(SessionStorageService);
  private ordersService = inject(OrdersService);
  private messageService = inject(MessageService);
  private dialogService = inject(DialogService);

  quotationId = signal<number | null>(null);
  loading = signal(false);
  saving = signal(false);
  submitted = signal(false);
  sourceOptions = signal<SourceOption[]>([]);
  customerOptions = signal<Customer[]>([]);
  contactResolving = signal(false);
  termsTemplates = signal<CrmQuotationTermsTemplate[]>([]);
  employeeOptions = signal<{ label: string; value: number }[]>([]);
  countries = signal<Country[]>([]);
  cities = signal<City[]>([]);
  vehicleTypes = signal<VehicleType[]>([]);
  vehicleConfigs = signal<VehicleConfig[]>([]);
  vehicleCapacities = signal<VehicleCapacity[]>([]);
  paymentTermOptions = signal<{ label: string; value: string }[]>([]);
  uomOptions = signal<{ label: string; value: string }[]>([]);
  regionOptions = signal<WarehousingMasterData[]>([]);
  warehouseOptions = signal<WarehousingMasterData[]>([]);
  tenantUomOptions = signal<WarehousingMasterData[]>([]);
  rateCardDialogVisible = signal(false);
  activeRateCardIndex = signal<number | null>(null);
  installationDialogVisible = signal(false);
  activeInstallationIndex = signal<number | null>(null);
  storageDialogVisible = signal(false);
  activeStorageIndex = signal<number | null>(null);
  selectedJobType = signal<CrmQuotationJobType>(CrmQuotationJobType.LOGISTICS);
  private totalVersion = signal(0);
  activeTab = signal<'general' | 'job' | 'lines' | 'terms' | 'remarks'>('general');
  private readonly tabKeys = ['general', 'job', 'lines', 'terms', 'remarks'] as const;
  private pendingSourceKey: string | null = null;
  private lastSyncedPosmProjectName = '';
  private lastSyncedPosmEventLocation = '';
  private lastAppliedTemplateDescription = '';
  private lastAppliedTemplateTermsHtml = '';
  private loadedCityCountryIds = new Set<number>();
  private loadingCityCountryIds = new Set<number>();
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  breadcrumbItems = signal<MenuItem[]>([]);

  jobTypeOptions = [
    { label: 'Logistics', value: CrmQuotationJobType.LOGISTICS },
    { label: 'Freight Forwarding & Customs', value: CrmQuotationJobType.CUSTOMS_CLEARANCE },
    { label: 'C&F Import Air', value: CrmQuotationJobType.CF_IMPORT_AIR },
    { label: 'C&F Import Sea', value: CrmQuotationJobType.CF_IMPORT_SEA },
    { label: 'C&F Import Land', value: CrmQuotationJobType.CF_IMPORT_LAND },
    { label: 'C&F Export', value: CrmQuotationJobType.CF_EXPORT },
    { label: 'C&F RORO', value: CrmQuotationJobType.CF_RORO },
    { label: 'C&F Consolidation', value: CrmQuotationJobType.CF_CONSOLIDATION },
    { label: 'POSM Installation', value: CrmQuotationJobType.POSM_INSTALLATION },
    { label: 'POSM Refurbishment', value: CrmQuotationJobType.POSM_REFURBISHMENT },
    { label: 'Storage', value: CrmQuotationJobType.STORAGE },
    { label: 'Task', value: CrmQuotationJobType.TASK },
    { label: 'Other', value: CrmQuotationJobType.OTHER }
  ];
  statusOptions = Object.values(CrmQuotationStatus).map(value => ({ label: value, value }));
  categoryOptions = ['PRODUCT', 'SERVICE', 'EXTERNAL'].map(value => ({ label: value, value }));
  discountOptions = [{ label: 'Amount', value: 'AMOUNT' }, { label: 'Percent', value: 'PERCENT' }];
  priorityOptions = ['NORMAL', 'HIGH', 'URGENT'].map(value => ({ label: value, value }));
  posmJobTypeOptions = ['INSTALLATION', 'REFURBISHMENT', 'LOGISTICS', 'OTHER'].map(value => ({ label: value, value }));
  rateTypeOptions = ['FULL_TRUCK', 'PALLET', 'DEDICATED', 'LTL', 'OTHER'].map(value => ({ label: value.replaceAll('_', ' '), value }));
  tenantUomSelectOptions = computed(() => this.tenantUomOptions()
    .filter(row => row.isActive !== false)
    .map(row => {
      const value = row.code || row.name;
      const label = row.code && row.code !== row.name ? `${row.name} - ${row.code}` : row.name;
      return value ? { label, value } : null;
    })
    .filter((option): option is { label: string; value: string } => !!option));
  unitBasisOptions = ['TRIP', 'PALLET', 'SQM', 'CBM', 'DAY', 'HOUR', 'EA'].map(value => ({ label: value, value }));
  storageChargeTypeOptions = ['STORAGE', 'HANDLING_IN', 'HANDLING_OUT', 'PALLETIZATION', 'OTHER'].map(value => ({ label: value.replaceAll('_', ' '), value }));
  storageMetricOptions = ['SQM', 'CBM', 'PALLET', 'DAY', 'EA'].map(value => ({ label: value, value }));
  cfServiceScopeOptions = ['FREIGHT_FORWARDING_CUSTOMS', 'AIR_FREIGHT_CLEARANCE', 'SEA_FREIGHT_CLEARANCE', 'LAND_BORDER_CLEARANCE', 'EXPORT_FORWARDING_CLEARANCE', 'RORO_CLEARANCE', 'CONSOLIDATION_CLEARANCE'].map(value => ({ label: value.replaceAll('_', ' '), value }));
  cfShipmentModeOptions = ['MULTIMODAL', 'AIR_IMPORT', 'SEA_IMPORT', 'LAND_IMPORT', 'EXPORT', 'RORO', 'SEA_AIR_CONSOLIDATION'].map(value => ({ label: value.replaceAll('_', ' '), value }));
  cfImportExportOptions = ['IMPORT', 'EXPORT', 'DOMESTIC'].map(value => ({ label: value, value }));
  yesNoOptions = [{ label: 'Yes', value: true }, { label: 'No', value: false }];

  isPosm = computed(() => [CrmQuotationJobType.POSM_INSTALLATION, CrmQuotationJobType.POSM_REFURBISHMENT].includes(this.selectedJobType()));
  isLogistics = computed(() => this.selectedJobType() === CrmQuotationJobType.LOGISTICS);
  isStorage = computed(() => this.selectedJobType() === CrmQuotationJobType.STORAGE);
  isPosmInstallation = computed(() => this.selectedJobType() === CrmQuotationJobType.POSM_INSTALLATION);
  showLogisticsRates = computed(() => this.isLogistics() || this.isPosmInstallation());
  showStorageRates = computed(() => this.isStorage() || this.isPosmInstallation());
  isCustoms = computed(() => this.isCf());
  isCf = computed(() => this.isCfJobType(this.selectedJobType()));
  totals = computed(() => {
    this.totalVersion();
    return this.calculateTotals();
  });
  formMetrics = computed(() => {
    const totals = this.totals();
    const currency = this.quotationForm.get('currencyCode')?.value || 'SAR';
    const source = this.selectedSourceOption();
    const contact = this.selectedContact();
    const contactName = contact?.name || this.quotationForm.get('pocName')?.value;
    const contactHint = contact?.emailAddress || this.quotationForm.get('pocEmail')?.value || 'Customer object required';

    return [
      {
        label: 'Quotation Value',
        value: `${currency} ${totals.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        hint: `${this.lines.length} line${this.lines.length === 1 ? '' : 's'}`,
        tone: 'success' as const
      },
      {
        label: 'Customer Source',
        value: source?.type || 'Pending',
        hint: source?.label || 'Select lead, prospect, or client',
        tone: source ? 'info' as const : 'warn' as const
      },
      {
        label: 'Contact',
        value: contactName || 'Pending',
        hint: contactHint,
        tone: contactName ? 'success' as const : 'warn' as const
      },
      {
        label: 'Job Type',
        value: this.selectedJobType().replaceAll('_', ' '),
        hint: this.isCf() ? 'C&F execution details required' : this.isPosm() ? 'POSM details required' : this.isLogistics() ? 'Logistics details required' : 'Generic quotation',
        tone: 'default' as const
      }
    ];
  });

  quotationForm = this.fb.group({
    id: [null as number | null],
    quotationNumber: [''],
    subject: ['', Validators.required],
    description: [''],
    countryId: [null as number | null],
    cityId: [null as number | null],
    sourceKey: [null as string | null, Validators.required],
    customerName: ['', Validators.required],
    customerId: [null as number | null],
    leadId: [null as number | null],
    prospectId: [null as number | null],
    clientId: [null as number | null],
    type: [CrmQuotationType.LOGISTICS, Validators.required],
    jobType: [CrmQuotationJobType.LOGISTICS, Validators.required],
    status: [CrmQuotationStatus.PREPARING, Validators.required],
    startDate: [new Date() as Date | string | null, Validators.required],
    expiryDate: [null as Date | string | null],
    currencyCode: ['SAR', Validators.required],
    exchangeRate: [1, Validators.required],
    vatRate: [15, Validators.required],
    discountType: ['AMOUNT' as 'AMOUNT' | 'PERCENT'],
    pricingNotes: [''],
    termsHtml: [''],
    paymentTerms: [''],
    pocName: ['', Validators.required],
    pocEmail: [''],
    pocPhoneNumber: [''],
    pocMobileNumber: [''],
    kamCustomerId: [null as number | null],
    companyId: [null as number | null],
    createdAt: [null as Date | null],
    createdByCustomerId: [null as number | null]
  });

  posmForm = this.fb.group({
    id: [null as number | null],
    projectName: [''],
    eventLocation: [''],
    eventCountryId: [null as number | null],
    eventCityId: [null as number | null],
    brandName: [''],
    jobPriority: ['NORMAL'],
    jobType: ['INSTALLATION']
  });

  logisticsForm = this.fb.group({
    id: [null as number | null],
    duration: [''],
    workingHours: [''],
    scope: [''],
    quantity: [1, [Validators.required, Validators.min(0.001)]],
    exclusions: [''],
    serviceScope: [''],
    shipmentMode: [''],
    importExportType: [''],
    blAwbNumber: [''],
    hblHawbNumber: [''],
    vesselOrFlight: [''],
    carrier: [''],
    originPort: [''],
    destinationPort: [''],
    etd: [null as Date | string | null],
    eta: [null as Date | string | null],
    deliveryOrderNumber: [''],
    bayanNumber: [''],
    undertakingNumber: [''],
    cargoDescription: [''],
    deliveryAddress: [''],
    clearanceRequired: [true],
    transportRequired: [true],
    containerTrackingRequired: [false]
  });

  logisticsRateCards = this.fb.array<FormGroup>([]);
  posmInstallationRows = this.fb.array<FormGroup>([]);
  storageRows = this.fb.array<FormGroup>([]);

  createLogisticsRateCard(value: any = {}) {
    return this.fb.group({
      rateId: [value.rateId ?? null as number | null],
      sourceCountryId: [value.sourceCountryId ?? null as number | null],
      sourceCityId: [value.sourceCityId ?? null as number | null],
      destinationCountryId: [value.destinationCountryId ?? null as number | null],
      destinationCityId: [value.destinationCityId ?? null as number | null],
      vehicleTypeId: [value.vehicleTypeId ?? null as number | null],
      vehicleConfigId: [value.vehicleConfigId ?? null as number | null],
      vehicleCapacityId: [value.vehicleCapacityId ?? null as number | null],
      distance: [value.distance ?? null as number | null],
      quantity: [value.quantity ?? 1, [Validators.required, Validators.min(0.001)]],
      amount: [value.amount ?? null as number | null],
      goodsInsurance: [value.goodsInsurance ?? false],
      portPermit: [value.portPermit ?? false],
      waitingThreshold: [value.waitingThreshold ?? null as number | null],
      waitingCharge: [value.waitingCharge ?? null as number | null],
      rateType: [value.rateType ?? 'FULL_TRUCK'],
      unitBasis: [value.unitBasis ?? 'TRIP'],
      transitTime: [value.transitTime ?? '']
    });
  }

  get logisticsRateCardControls() {
    return this.logisticsRateCards.controls as FormGroup[];
  }

  addLogisticsRateCard(value: any = {}) {
    this.logisticsRateCards.push(this.createLogisticsRateCard(value));
    this.syncJobTypeBehavior();
  }

  openNewLogisticsRateCard() {
    this.addLogisticsRateCard({ lineNumber: this.logisticsRateCards.length + 1 });
    this.openLogisticsRateCardDialog(this.logisticsRateCards.length - 1);
  }

  openLogisticsRateCardDialog(index: number) {
    this.activeRateCardIndex.set(index);
    this.rateCardDialogVisible.set(true);
  }

  closeLogisticsRateCardDialog() {
    this.rateCardDialogVisible.set(false);
    this.activeRateCardIndex.set(null);
  }

  activeRateCardForm() {
    const index = this.activeRateCardIndex();
    return index === null ? null : this.logisticsRateCards.at(index) as FormGroup;
  }

  removeLogisticsRateCard(index: number) {
    this.logisticsRateCards.removeAt(index);
    this.syncJobTypeBehavior();
    this.refreshTotals();
  }

  createPosmInstallationRow(value: any = {}) {
    return this.fb.group({
      id: [value.id ?? null as number | null],
      lineNumber: [value.lineNumber ?? null as number | null],
      description: [value.description ?? '', Validators.required],
      dimensions: [value.dimensions ?? ''],
      weight: [value.weight ?? null as number | null],
      quantity: [value.quantity ?? 1],
      installationCost: [value.installationCost ?? 0, Validators.required],
      remarks: [value.remarks ?? '']
    });
  }

  get posmInstallationRowControls() {
    return this.posmInstallationRows.controls as FormGroup[];
  }

  addPosmInstallationRow(value: any = {}) {
    this.posmInstallationRows.push(this.createPosmInstallationRow(value));
    this.syncJobTypeBehavior();
  }

  openNewInstallationRow() {
    this.addPosmInstallationRow({ lineNumber: this.posmInstallationRows.length + 1 });
    this.openInstallationDialog(this.posmInstallationRows.length - 1);
  }

  openInstallationDialog(index: number) {
    this.activeInstallationIndex.set(index);
    this.installationDialogVisible.set(true);
  }

  closeInstallationDialog() {
    this.installationDialogVisible.set(false);
    this.activeInstallationIndex.set(null);
  }

  activeInstallationForm() {
    const index = this.activeInstallationIndex();
    return index === null ? null : this.posmInstallationRows.at(index) as FormGroup;
  }

  removePosmInstallationRow(index: number) {
    this.posmInstallationRows.removeAt(index);
    this.syncJobTypeBehavior();
    this.refreshTotals();
  }

  createStorageRow(value: any = {}) {
    return this.fb.group({
      id: [value.id ?? null as number | null],
      lineNumber: [value.lineNumber ?? null as number | null],
      chargeType: [value.chargeType ?? 'STORAGE', Validators.required],
      description: [value.description ?? ''],
      metric: [value.metric ?? 'SQM', Validators.required],
      rate: [value.rate ?? 0, Validators.required],
      quantity: [value.quantity ?? 1],
      amount: [value.amount ?? null as number | null],
      regionId: [value.regionId ?? null as number | null],
      warehouseId: [value.warehouseId ?? null as number | null],
      remarks: [value.remarks ?? '']
    });
  }

  get storageRowControls() {
    return this.storageRows.controls as FormGroup[];
  }

  addStorageRow(value: any = {}) {
    this.storageRows.push(this.createStorageRow(value));
    this.syncJobTypeBehavior();
  }

  openNewStorageRow() {
    this.addStorageRow({ lineNumber: this.storageRows.length + 1 });
    this.openStorageDialog(this.storageRows.length - 1);
  }

  openStorageDialog(index: number) {
    this.activeStorageIndex.set(index);
    this.storageDialogVisible.set(true);
  }

  closeStorageDialog() {
    this.storageDialogVisible.set(false);
    this.activeStorageIndex.set(null);
  }

  activeStorageForm() {
    const index = this.activeStorageIndex();
    return index === null ? null : this.storageRows.at(index) as FormGroup;
  }

  removeStorageRow(index: number) {
    this.storageRows.removeAt(index);
    this.syncJobTypeBehavior();
    this.refreshTotals();
  }

  lines = this.fb.array<FormGroup>([]);

  ngOnInit() {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (id) {
      this.quotationId.set(id);
      this.setBreadcrumb('Edit Quotation');
    } else {
      this.setBreadcrumb('New Quotation');
      this.pendingSourceKey = this.sourceKeyFromQueryParams();
      this.quotationForm.patchValue({
        companyId: this.toolbarService.customer()?.company?.id ?? null,
        createdByCustomerId: this.toolbarService.customer()?.id ?? null
      });
    }

    this.loadOptions();
    this.loadRateCardOptions();
    this.route.queryParamMap.subscribe(params => {
      this.activeTab.set(this.resolveTab(params.get('tab')));
    });
    this.quotationForm.get('sourceKey')?.valueChanges.subscribe(value => this.applySource(value));
    this.quotationForm.get('customerId')?.valueChanges.subscribe(value => this.applyContact(value));
    this.quotationForm.get('subject')?.valueChanges.subscribe(() => this.syncPosmFromGeneral());
    this.quotationForm.get('countryId')?.valueChanges.subscribe(value => this.onGeneralCountryChange(value));
    this.quotationForm.get('cityId')?.valueChanges.subscribe(() => this.syncPosmFromGeneral());
    this.posmForm.get('eventCountryId')?.valueChanges.subscribe(value => this.onPosmCountryChange(value));
    this.posmForm.get('eventCityId')?.valueChanges.subscribe(value => this.onPosmCityChange(value));
    this.quotationForm.get('jobType')?.valueChanges.subscribe(value => {
      this.selectedJobType.set((value as CrmQuotationJobType) || CrmQuotationJobType.LOGISTICS);
      this.applyTermsTemplateForJobType(this.selectedJobType());
      this.syncJobTypeBehavior();
      this.syncPosmFromGeneral();
      this.refreshTotals();
    });
    [this.quotationForm, this.logisticsForm, this.logisticsRateCards, this.posmInstallationRows, this.storageRows, this.lines]
      .forEach(control => this.watchTotalInputs(control));
    this.syncJobTypeBehavior();

    if (id) this.loadQuotation(id);
  }

  get lineControls() {
    return this.lines.controls;
  }

  isInvalid(form: FormGroup, controlName: string) {
    return this.isControlInvalid(form.get(controlName));
  }

  isLineInvalid(index: number, controlName: string) {
    return this.isControlInvalid(this.lines.at(index)?.get(controlName));
  }

  isRateCardInvalid(index: number, controlName: string) {
    return this.isControlInvalid(this.logisticsRateCards.at(index)?.get(controlName));
  }

  isPosmInstallationRowInvalid(index: number, controlName: string) {
    return this.isControlInvalid(this.posmInstallationRows.at(index)?.get(controlName));
  }

  isStorageRowInvalid(index: number, controlName: string) {
    return this.isControlInvalid(this.storageRows.at(index)?.get(controlName));
  }

  isControlInvalid(control: AbstractControl | null | undefined) {
    return !!control && control.invalid && (control.touched || control.dirty || this.submitted());
  }

  quotationReadiness() {
    const items = [
      this.readinessItem('subject', 'Subject', 'pi pi-file-edit', 'Enter the quotation subject.', this.controlReady(this.quotationForm, 'subject')),
      this.readinessItem('source', 'Customer Source', 'pi pi-link', 'Select a lead, prospect, or client.', this.controlReady(this.quotationForm, 'sourceKey')),
      this.readinessItem('contact', 'Quotation Contact', 'pi pi-user', 'Select or create a quotation contact.', this.controlReady(this.quotationForm, 'pocName')),
      this.readinessItem('jobType', 'Job Type', 'pi pi-tags', 'Select the quotation job type.', this.controlReady(this.quotationForm, 'jobType')),
      this.readinessItem('startDate', 'Start Date', 'pi pi-calendar', 'Select the quotation start date.', this.controlReady(this.quotationForm, 'startDate')),
      this.readinessItem('currency', 'Currency', 'pi pi-money-bill', 'Enter the quotation currency.', this.controlReady(this.quotationForm, 'currencyCode'))
    ];

    if (this.isPosm()) {
      items.push(this.readinessItem('posmProjectName', 'POSM Job Name', 'pi pi-briefcase', 'Enter the POSM job name.', this.controlReady(this.posmForm, 'projectName')));
    }
    if (this.isLogistics() || this.logisticsRateCards.length > 0) {
      items.push(this.readinessItem(
        'logisticsRates',
        'Logistics Rate Card',
        'pi pi-truck',
        this.logisticsRateCards.length ? 'Complete or remove incomplete logistics rate cards.' : 'Add at least one logistics delivery rate card.',
        this.logisticsRateCards.length > 0 && this.logisticsRateCards.valid
      ));
    }
    if (this.isCf()) {
      items.push(this.readinessItem(
        'cfDetails',
        'C&F Details',
        'pi pi-compass',
        'Enter service scope, shipment mode, origin, and destination.',
        this.logisticsForm.valid
      ));
    }
    if (this.isPosm()) {
      items.push(this.readinessItem(
        'posmRates',
        'POSM Line',
        'pi pi-wrench',
        this.posmInstallationRows.length ? 'Complete or remove incomplete POSM lines.' : 'Add at least one POSM line.',
        this.posmInstallationRows.length > 0 && this.posmInstallationRows.valid
      ));
    }
    if (this.isStorage() || this.storageRows.length > 0) {
      items.push(this.readinessItem(
        'storageRates',
        'Storage Rate',
        'pi pi-warehouse',
        this.storageRows.length ? 'Complete or remove incomplete storage rates.' : 'Add at least one warehousing storage rate.',
        this.storageRows.length > 0 && this.storageRows.valid
      ));
    }

    const pendingItems = items.filter(item => !item.ready);
    return {
      items,
      pendingItems,
      pendingCount: pendingItems.length,
      ready: pendingItems.length === 0,
      summary: pendingItems.length === 1 ? pendingItems[0].hint : `${pendingItems.length} compulsory items need attention.`
    };
  }

  quotationReadinessPercent() {
    const items = this.quotationReadiness().items;
    return Math.round(items.filter(item => item.ready).length / Math.max(items.length, 1) * 100);
  }

  selectedSourceOption() {
    return this.sourceOptions().find(option => option.value === this.quotationForm.get('sourceKey')?.value) || null;
  }

  createLine(value: any = {}) {
    return this.fb.group({
      id: [value.id ?? null],
      lineNumber: [value.lineNumber ?? null],
      description: [value.description ?? ''],
      itemCategory: [value.itemCategory ?? 'SERVICE'],
      uom: [value.uom ?? 'EA'],
      quantity: [value.quantity ?? 1, [Validators.min(0)]],
      unitPrice: [value.unitPrice ?? 0, [Validators.min(0)]],
      unitDiscount: [value.unitDiscount ?? 0],
      taxRate: [value.taxRate ?? 15]
    });
  }

  addLine() {
    this.lines.push(this.createLine({ lineNumber: this.lines.length + 1 }));
    this.refreshTotals();
  }

  removeLine(index: number) {
    this.lines.removeAt(index);
    this.refreshTotals();
  }

  loadOptions() {
    const companyId = this.toolbarService.customer()?.company?.id;
    if (!companyId) return;
    forkJoin({
      clients: this.crmService.getClients(companyId),
      prospects: this.crmService.getProspects(companyId),
      leads: this.crmService.getLeads(companyId),
      employees: this.partyService.getParties(companyId, ['Employee'], '', 0, 500),
      regions: this.warehousingService.getMasterData('REGION'),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      tenantUoms: this.warehousingService.getMasterData('TENANT_UOM'),
      customers: this.customerService.getCustomersByCompany(companyId),
      termsTemplates: this.crmService.getQuotationTermsTemplates(companyId),
      paymentTerms: this.ordersService.getAllPaymentTerms()
    }).subscribe(({ clients, prospects, leads, employees, regions, warehouses, tenantUoms, customers, termsTemplates, paymentTerms }) => {
      const options: SourceOption[] = [];
      options.push(...clients.map(record => ({ label: `Client - ${this.clientLabel(record)}`, value: `CLIENT:${record.id}`, type: 'CLIENT' as SourceType, id: record.id, record })));
      options.push(...prospects.map(record => ({ label: `Prospect - ${record.companyName}`, value: `PROSPECT:${record.id}`, type: 'PROSPECT' as SourceType, id: record.id, record })));
      options.push(...leads.map(record => ({ label: `Lead - ${this.leadLabel(record)}`, value: `LEAD:${record.id}`, type: 'LEAD' as SourceType, id: record.id, record })));
      this.sourceOptions.set(options);
      this.customerOptions.set(customers || []);
      this.termsTemplates.set(termsTemplates || []);
      this.employeeOptions.set((employees.content || []).map(party => ({ label: party.entityName, value: party.id.entityId })));
      this.regionOptions.set(regions.data || []);
      this.warehouseOptions.set(warehouses.data || []);
      this.tenantUomOptions.set(tenantUoms.data || []);
      this.paymentTermOptions.set((paymentTerms || []).map(term => ({ label: this.paymentTermLabel(term), value: term.name })));
      this.applyPendingSourceKey();
      if (!this.quotationId()) {
        this.applyTermsTemplateForJobType(this.selectedJobType());
      }
    });
  }

  loadQuotation(id: number) {
    this.loading.set(true);
    this.crmService.getQuotation(id).pipe(finalize(() => this.loading.set(false))).subscribe(response => {
      if (!response.success || !response.data) return;
      const quote = response.data;
      this.quotationForm.patchValue({
        ...quote,
        sourceKey: this.sourceKey(quote),
        startDate: quote.startDate ? new Date(quote.startDate) : new Date(),
        expiryDate: quote.expiryDate ? new Date(quote.expiryDate) : null
      }, { emitEvent: false });
      this.loadCitiesForCountry(quote.countryId, () => {
        this.syncGeneralCountryFromCity();
        this.syncPosmFromGeneral();
      });
      this.selectedJobType.set((quote.jobType as CrmQuotationJobType) || CrmQuotationJobType.LOGISTICS);
      this.setBreadcrumb(quote.quotationNumber || quote.subject || 'Edit Quotation');
      this.lines.clear();
      (quote.lines || []).forEach(line => this.lines.push(this.createLine(line)));
      if (quote.posmDetails) {
        this.posmForm.patchValue({
          id: quote.posmDetails.id ?? null,
          projectName: quote.posmDetails.projectName || '',
          eventLocation: quote.posmDetails.eventLocation || '',
          brandName: quote.posmDetails.brandName || '',
          jobPriority: quote.posmDetails.jobPriority || 'NORMAL',
          jobType: quote.posmDetails.jobType || 'INSTALLATION'
        });
        this.syncPosmEventCityFromLocation();
      }
      if (quote.logisticsDetails) {
        this.patchLogisticsDetails(quote.logisticsDetails);
        this.loadCitiesForRateCards();
      }
      if (quote.posmInstallationDetails?.rows?.length) {
        this.posmInstallationRows.clear();
        quote.posmInstallationDetails.rows.forEach(row => this.addPosmInstallationRow(row));
      }
      if (quote.storageDetails?.rows?.length) {
        this.storageRows.clear();
        quote.storageDetails.rows.forEach(row => this.addStorageRow(row));
      }
      this.syncJobTypeBehavior();
      this.refreshTotals();
    });
  }

  applySource(sourceKey: string | null) {
    const option = this.sourceOptions().find(item => item.value === sourceKey);
    if (!option) {
      this.quotationForm.patchValue({ customerName: '', customerId: null, leadId: null, prospectId: null, clientId: null }, { emitEvent: false });
      return;
    }
    const record: any = option.record;
    this.quotationForm.patchValue({
      customerName: record.companyName,
      customerId: record.linkedCustomerId || null,
      pocName: record.notifyingPersonName || record.pocName || '',
      pocEmail: record.primaryEmail || record.pocEmail || '',
      pocPhoneNumber: record.phoneNumber || record.pocPhoneNumber || '',
      pocMobileNumber: record.mobileNumber || '',
      kamCustomerId: record.kamCustomerId ?? null,
      currencyCode: record.currencyCode || 'SAR',
      leadId: option.type === 'LEAD' ? option.id! : null,
      prospectId: option.type === 'PROSPECT' ? option.id! : null,
      clientId: option.type === 'CLIENT' ? option.id! : null
    }, { emitEvent: false });
    this.resolveContactFromSource(record);
  }

  syncJobTypeBehavior() {
    const jobType = this.selectedJobType();
    this.quotationForm.get('type')?.setValue(this.mapJobTypeToType(jobType), { emitEvent: false });
    if (this.isCf()) {
      this.applyCfDefaults(jobType);
    }
    this.syncPosmFromGeneral();

    const projectName = this.posmForm.get('projectName');
    if (this.isPosm()) {
      projectName?.addValidators(Validators.required);
    } else {
      projectName?.clearValidators();
    }
    projectName?.updateValueAndValidity({ emitEvent: false });

    this.logisticsRateCardControls.forEach(rateCard => {
      ['sourceCountryId', 'sourceCityId', 'destinationCountryId', 'destinationCityId', 'vehicleTypeId', 'amount'].forEach(controlName => {
        const control = rateCard.get(controlName);
        if (this.isLogistics() || this.logisticsRateCards.length > 0) {
          control?.addValidators(Validators.required);
        } else {
          control?.clearValidators();
        }
        control?.updateValueAndValidity({ emitEvent: false });
      });
    });
    this.posmInstallationRowControls.forEach(row => {
      ['description', 'installationCost'].forEach(controlName => {
        const control = row.get(controlName);
        if (this.isPosm()) {
          control?.addValidators(Validators.required);
        } else {
          control?.clearValidators();
        }
        control?.updateValueAndValidity({ emitEvent: false });
      });
    });
    this.storageRowControls.forEach(row => {
      ['chargeType', 'metric', 'rate'].forEach(controlName => {
        const control = row.get(controlName);
        if (this.isStorage() || this.storageRows.length > 0) {
          control?.addValidators(Validators.required);
        } else {
          control?.clearValidators();
        }
        control?.updateValueAndValidity({ emitEvent: false });
      });
    });
    ['serviceScope', 'shipmentMode', 'originPort', 'destinationPort'].forEach(controlName => {
      const control = this.logisticsForm.get(controlName);
      if (this.isCf()) {
        control?.addValidators(Validators.required);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  save() {
    if (this.saving()) {
      return;
    }
    this.submitted.set(true);
    if (this.contactResolving()) {
      this.messageService.add({ severity: 'info', summary: 'Contact', detail: 'Resolving the quotation contact. Please save again once the contact is selected.' });
      return;
    }
    this.pruneEmptyRows();
    this.syncJobTypeBehavior();
    const readiness = this.quotationReadiness();
    if (this.quotationForm.invalid || !readiness.ready || (this.isPosm() && this.posmForm.invalid) || (this.isCf() && this.logisticsForm.invalid) || (this.showLogisticsRates() && this.logisticsRateCards.invalid)
      || (this.isPosm() && this.posmInstallationRows.invalid) || (this.showStorageRates() && this.storageRows.invalid)) {
      this.quotationForm.markAllAsTouched();
      this.lines.markAllAsTouched();
      this.posmForm.markAllAsTouched();
      this.logisticsForm.markAllAsTouched();
      this.logisticsRateCards.markAllAsTouched();
      this.posmInstallationRows.markAllAsTouched();
      this.storageRows.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'There are validation errors. Please review the highlighted fields.' });
      return;
    }
    const totals = this.calculateTotals();
    const { sourceKey: _sourceKey, countryId: _countryId, ...quotationValues } = this.quotationForm.value as any;
    const { eventCountryId: _eventCountryId, eventCityId: _eventCityId, ...posmValues } = this.posmForm.value as any;
    const selectedPosmCity = this.cityById(_eventCityId);
    const posmEventLocation = selectedPosmCity ? this.cityLabel(selectedPosmCity).trim() : (posmValues.eventLocation || '').trim();
    const quote: CrmQuotation = {
      ...quotationValues,
      subTotal: totals.subTotal,
      discountAmount: totals.discountAmount,
      vatAmount: totals.vatAmount,
      totalAmount: totals.totalAmount,
      updatedByCustomerId: this.quotationId() ? this.toolbarService.customer()?.id : undefined,
      createdByCustomerId: this.quotationId() ? this.quotationForm.get('createdByCustomerId')?.value ?? undefined : this.toolbarService.customer()?.id,
      lines: this.lines.value.map((line: any, index: number) => ({
        ...line,
        lineNumber: line.lineNumber || index + 1,
        totalLineAmount: this.lineTotal(line)
      })),
      posmDetails: this.isPosm() ? {
        ...posmValues,
        eventLocation: posmEventLocation,
        brandName: (posmValues.brandName || '').trim()
      } as any : undefined,
      logisticsDetails: this.shouldSaveLogisticsDetails() ? this.buildLogisticsDetails() : undefined,
      posmInstallationDetails: this.shouldSavePosmInstallationDetails() ? this.buildPosmInstallationDetails() : undefined,
      storageDetails: this.shouldSaveStorageDetails() ? this.buildStorageDetails() : undefined
    };
    this.saving.set(true);
    this.crmService.saveQuotation(quote).pipe(finalize(() => this.saving.set(false))).subscribe(response => {
      if (response.success && response.data) {
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Quotation saved successfully.' });
        this.router.navigate(['/crm/quotations', response.data.id]);
        return;
      }
      this.messageService.add({ severity: 'error', summary: 'Save failed', detail: response.message || 'The quotation could not be saved.' });
    }, error => {
      const detail = error?.error?.message
        || (typeof error?.error === 'string' ? error.error : '')
        || 'The quotation could not be saved. Please try again.';
      this.messageService.add({ severity: 'error', summary: 'Save failed', detail });
    });
  }

  contactLabel(customer: Customer) {
    return [customer.name, customer.emailAddress, customer.phoneNumber].filter(Boolean).join(' - ') || `Customer #${customer.id}`;
  }

  selectedContact() {
    const customerId = this.quotationForm.get('customerId')?.value;
    return this.customerOptions().find(customer => customer.id === customerId) || null;
  }

  openContactDialog() {
    const ref = this.dialogService.open(QuickContactDialogComponent, {
      header: 'New Quotation Contact',
      modal: true,
      width: '34rem',
      breakpoints: { '640px': '94vw' },
      styleClass: 'quotation-contact-dialog'
    });

    ref.onClose.subscribe((contact: any) => {
      if (!contact) return;
      this.customerOptions.set([...this.customerOptions(), contact].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      this.quotationForm.patchValue({
        customerId: contact.id,
        pocName: contact.name,
        pocEmail: contact.emailAddress || '',
        pocPhoneNumber: contact.phoneNumber || '',
        pocMobileNumber: ''
      }, { emitEvent: false });
      this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Contact created successfully.' });
    });
  }

  calculateTotals() {
    const lines = this.lines.value as any[];
    const structuredSubTotal = this.structuredSubTotal();
    const subTotal = structuredSubTotal + lines.reduce((sum, line) => sum + this.lineBase(line), 0);
    const discountAmount = lines.reduce((sum, line) => sum + this.lineDiscount(line), 0);
    const vatAmount = this.round(structuredSubTotal * Number(this.quotationForm.get('vatRate')?.value || 0) / 100)
      + lines.reduce((sum, line) => sum + this.lineTax(line), 0);
    return {
      subTotal: this.round(subTotal),
      discountAmount: this.round(discountAmount),
      vatAmount: this.round(vatAmount),
      totalAmount: this.round(subTotal - discountAmount + vatAmount)
    };
  }

  structuredSubTotal() {
    const logisticsQuantity = Number(this.logisticsForm.get('quantity')?.value || 1);
    const logisticsTotal = this.showLogisticsRates()
      ? (this.logisticsRateCards.value as any[]).reduce((sum, row) => sum + this.logisticsRateCardTotal(row, logisticsQuantity), 0)
      : 0;
    const storageTotal = this.showStorageRates()
      ? (this.storageRows.value as any[]).reduce((sum, row) => sum + this.storageAmount(row), 0)
      : 0;
    if (this.isPosm()) {
      const installationTotal = (this.posmInstallationRows.value as any[]).reduce((sum, row) => sum + Number(row.quantity || 1) * Number(row.installationCost || 0), 0);
      return logisticsTotal + installationTotal + storageTotal;
    }
    if (this.isLogistics()) {
      return logisticsTotal;
    }
    if (this.isStorage()) {
      return storageTotal;
    }
    return 0;
  }

  storageAmount(row: any) {
    return Number(row.amount ?? (Number(row.rate || 0) * Number(row.quantity || 1)));
  }

  logisticsRateCardsTotal() {
    const logisticsQuantity = Number(this.logisticsForm.get('quantity')?.value || 1);
    return (this.logisticsRateCards.value as any[]).reduce((sum, row) => sum + this.logisticsRateCardTotal(row, logisticsQuantity), 0);
  }

  logisticsRateCardTotal(row: any, quantity = Number(this.logisticsForm.get('quantity')?.value || 1)) {
    return Number(row?.amount || 0) * quantity;
  }

  lineBase(line: any) {
    return Number(line.quantity || 0) * Number(line.unitPrice || 0);
  }

  lineDiscount(line: any) {
    return Number(line.quantity || 0) * Number(line.unitDiscount || 0);
  }

  lineTax(line: any) {
    return Math.max(0, this.lineBase(line) - this.lineDiscount(line)) * Number(line.taxRate || 0) / 100;
  }

  lineTotal(line: any) {
    return this.round(this.lineBase(line) - this.lineDiscount(line) + this.lineTax(line));
  }

  round(value: number) {
    return Math.round(value * 100) / 100;
  }

  private refreshTotals() {
    this.totalVersion.update(version => version + 1);
  }

  private watchTotalInputs(control: AbstractControl) {
    control.valueChanges.subscribe(() => this.refreshTotals());
  }

  cancel() {
    const id = this.quotationId();
    this.router.navigate(id ? ['/crm/quotations', id] : ['/crm/quotations']);
  }

  setTab(tab: 'general' | 'job' | 'lines' | 'terms' | 'remarks') {
    this.activeTab.set(tab);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private resolveTab(tab: string | null): 'general' | 'job' | 'lines' | 'terms' | 'remarks' {
    return (this.tabKeys.find(value => value === tab) || 'general') as 'general' | 'job' | 'lines' | 'terms' | 'remarks';
  }

  onLogisticsVehicleTypeChange(index: number) {
    this.logisticsRateCards.at(index)?.patchValue({ vehicleConfigId: null, vehicleCapacityId: null }, { emitEvent: false });
  }

  onSourceCountryChange(index: number) {
    const rateCard = this.logisticsRateCards.at(index);
    rateCard?.patchValue({ sourceCityId: null, distance: null }, { emitEvent: false });
    this.loadCitiesForCountry(rateCard?.get('sourceCountryId')?.value);
  }

  onDestinationCountryChange(index: number) {
    const rateCard = this.logisticsRateCards.at(index);
    rateCard?.patchValue({ destinationCityId: null, distance: null }, { emitEvent: false });
    this.loadCitiesForCountry(rateCard?.get('destinationCountryId')?.value);
  }

  onSourceCityChange(index: number) {
    this.updateRateCardDistance(index);
  }

  onDestinationCityChange(index: number) {
    this.updateRateCardDistance(index);
  }

  onGeneralCountryChange(countryId?: number | null) {
    const cityId = this.quotationForm.get('cityId')?.value;
    if (!countryId || (cityId && !this.cityBelongsToCountry(cityId, countryId))) {
      this.quotationForm.patchValue({ cityId: null }, { emitEvent: true });
    }
    this.loadCitiesForCountry(countryId, () => this.syncPosmFromGeneral());
  }

  onPosmCountryChange(countryId?: number | null) {
    const cityId = this.posmForm.get('eventCityId')?.value;
    if (!countryId || (cityId && !this.cityBelongsToCountry(cityId, countryId))) {
      this.posmForm.patchValue({ eventCityId: null, eventLocation: '' }, { emitEvent: false });
      this.lastSyncedPosmEventLocation = '';
    }
    this.loadCitiesForCountry(countryId);
  }

  onPosmCityChange(cityId?: number | null) {
    const city = this.cityById(cityId);
    const location = city ? this.cityLabel(city).trim() : '';
    this.posmForm.patchValue({ eventLocation: location }, { emitEvent: false });
    this.lastSyncedPosmEventLocation = location;
  }

  cityLabel(city?: City | number | null) {
    if (typeof city === 'number') {
      city = this.cityById(city);
    }
    return city ? [city.cityName, city.country?.name].filter(Boolean).join(', ') : '';
  }

  countryLabel(countryId?: number | null) {
    return this.countryById(countryId)?.name || '-';
  }

  cityName(cityId?: number | null) {
    return this.cityById(cityId)?.cityName || '-';
  }

  generalCityOptions() {
    const countryId = this.quotationForm.get('countryId')?.value;
    return countryId ? this.citiesForCountry(countryId) : [];
  }

  posmCityOptions() {
    const countryId = this.posmForm.get('eventCountryId')?.value;
    return countryId ? this.citiesForCountry(countryId) : [];
  }

  vehicleTypeName(vehicleTypeId?: number | null) {
    return this.vehicleTypes().find(type => type.id === vehicleTypeId)?.name || '-';
  }

  vehicleConfigName(configId?: number | null) {
    return this.vehicleConfigs().find(config => config.configId === configId)?.name || '-';
  }

  vehicleCapacityName(capacityId?: number | null) {
    return this.vehicleCapacities().find(capacity => capacity.capacityId === capacityId)?.name || '-';
  }

  lineAmount(value?: number | null) {
    return Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  rateCardTitle(rateCard: FormGroup, index: number) {
    const value = rateCard.value;
    const source = this.cityName(value.sourceCityId);
    const destination = this.cityName(value.destinationCityId);
    return source !== '-' || destination !== '-' ? `${source} -> ${destination}` : `Rate Card ${index + 1}`;
  }

  paymentTermLabel(term: PaymentTerm) {
    return [term.name, term.days ? `${term.days} days` : null].filter(Boolean).join(' - ');
  }

  private readinessItem(key: string, label: string, icon: string, hint: string, ready: boolean) {
    return { key, label, icon, hint, ready };
  }

  private controlReady(form: FormGroup, controlName: string) {
    return !!form.get(controlName)?.valid;
  }

  private pruneEmptyRows() {
    this.removeEmptyControls(this.lines, value => this.isEmptyLine(value));
    this.removeEmptyControls(this.logisticsRateCards, value => this.isEmptyLogisticsRateCard(value));
    this.removeEmptyControls(this.posmInstallationRows, value => this.isEmptyPosmInstallationRow(value));
    this.removeEmptyControls(this.storageRows, value => this.isEmptyStorageRow(value));
  }

  private removeEmptyControls(array: FormArray, isEmpty: (value: any) => boolean) {
    for (let index = array.length - 1; index >= 0; index--) {
      if (isEmpty(array.at(index).value)) {
        array.removeAt(index);
      }
    }
  }

  private isEmptyLine(line: any) {
    return !line?.id
      && !this.hasText(line?.description)
      && !this.hasPositiveNumber(line?.unitPrice)
      && !this.hasPositiveNumber(line?.unitDiscount)
      && !this.hasMeaningfulQuantity(line?.quantity, 1);
  }

  private isEmptyLogisticsRateCard(rateCard: any) {
    return !rateCard?.rateId
      && !rateCard?.sourceCountryId
      && !rateCard?.sourceCityId
      && !rateCard?.destinationCountryId
      && !rateCard?.destinationCityId
      && !rateCard?.vehicleTypeId
      && !rateCard?.vehicleConfigId
      && !rateCard?.vehicleCapacityId
      && !this.hasPositiveNumber(rateCard?.distance)
      && !this.hasPositiveNumber(rateCard?.amount)
      && !this.hasPositiveNumber(rateCard?.waitingThreshold)
      && !this.hasPositiveNumber(rateCard?.waitingCharge)
      && !this.hasText(rateCard?.transitTime)
      && !rateCard?.goodsInsurance
      && !rateCard?.portPermit;
  }

  private isEmptyPosmInstallationRow(row: any) {
    return !row?.id
      && !this.hasText(row?.description)
      && !this.hasText(row?.dimensions)
      && !this.hasPositiveNumber(row?.weight)
      && !this.hasPositiveNumber(row?.installationCost)
      && !this.hasMeaningfulQuantity(row?.quantity, 1)
      && !this.hasText(row?.remarks);
  }

  private isEmptyStorageRow(row: any) {
    return !row?.id
      && !this.hasText(row?.description)
      && !this.hasPositiveNumber(row?.rate)
      && !this.hasPositiveNumber(row?.amount)
      && !this.hasMeaningfulQuantity(row?.quantity, 1)
      && !row?.regionId
      && !row?.warehouseId
      && !this.hasText(row?.remarks);
  }

  private shouldSaveLogisticsDetails() {
    return this.isCf()
      || this.logisticsRateCards.length > 0
      || this.hasText(this.logisticsForm.get('duration')?.value)
      || this.hasText(this.logisticsForm.get('workingHours')?.value)
      || this.hasText(this.logisticsForm.get('scope')?.value)
      || this.hasText(this.logisticsForm.get('exclusions')?.value);
  }

  private isCfJobType(jobType?: CrmQuotationJobType | null) {
    return jobType === CrmQuotationJobType.CUSTOMS_CLEARANCE
      || jobType === CrmQuotationJobType.CF_IMPORT_AIR
      || jobType === CrmQuotationJobType.CF_IMPORT_SEA
      || jobType === CrmQuotationJobType.CF_IMPORT_LAND
      || jobType === CrmQuotationJobType.CF_EXPORT
      || jobType === CrmQuotationJobType.CF_RORO
      || jobType === CrmQuotationJobType.CF_CONSOLIDATION;
  }

  private applyCfDefaults(jobType: CrmQuotationJobType) {
    const defaults = this.cfDefaults(jobType);
    const patch: any = {};
    if (!this.logisticsForm.get('serviceScope')?.value) patch.serviceScope = defaults.serviceScope;
    if (!this.logisticsForm.get('shipmentMode')?.value) patch.shipmentMode = defaults.shipmentMode;
    if (!this.logisticsForm.get('importExportType')?.value) patch.importExportType = defaults.importExportType;
    if (this.logisticsForm.get('clearanceRequired')?.value === null || this.logisticsForm.get('clearanceRequired')?.value === undefined) patch.clearanceRequired = true;
    if (this.logisticsForm.get('transportRequired')?.value === null || this.logisticsForm.get('transportRequired')?.value === undefined) patch.transportRequired = true;
    if (this.logisticsForm.get('containerTrackingRequired')?.value === null || this.logisticsForm.get('containerTrackingRequired')?.value === undefined) patch.containerTrackingRequired = defaults.containerTrackingRequired;
    if (Object.keys(patch).length) {
      this.logisticsForm.patchValue(patch, { emitEvent: false });
    }
  }

  private cfDefaults(jobType: CrmQuotationJobType) {
    switch (jobType) {
      case CrmQuotationJobType.CF_IMPORT_AIR:
        return { serviceScope: 'AIR_FREIGHT_CLEARANCE', shipmentMode: 'AIR_IMPORT', importExportType: 'IMPORT', containerTrackingRequired: false };
      case CrmQuotationJobType.CF_IMPORT_SEA:
        return { serviceScope: 'SEA_FREIGHT_CLEARANCE', shipmentMode: 'SEA_IMPORT', importExportType: 'IMPORT', containerTrackingRequired: true };
      case CrmQuotationJobType.CF_IMPORT_LAND:
        return { serviceScope: 'LAND_BORDER_CLEARANCE', shipmentMode: 'LAND_IMPORT', importExportType: 'IMPORT', containerTrackingRequired: false };
      case CrmQuotationJobType.CF_EXPORT:
        return { serviceScope: 'EXPORT_FORWARDING_CLEARANCE', shipmentMode: 'EXPORT', importExportType: 'EXPORT', containerTrackingRequired: false };
      case CrmQuotationJobType.CF_RORO:
        return { serviceScope: 'RORO_CLEARANCE', shipmentMode: 'RORO', importExportType: 'IMPORT', containerTrackingRequired: false };
      case CrmQuotationJobType.CF_CONSOLIDATION:
        return { serviceScope: 'CONSOLIDATION_CLEARANCE', shipmentMode: 'SEA_AIR_CONSOLIDATION', importExportType: 'IMPORT', containerTrackingRequired: true };
      default:
        return { serviceScope: 'FREIGHT_FORWARDING_CUSTOMS', shipmentMode: 'MULTIMODAL', importExportType: 'IMPORT', containerTrackingRequired: false };
    }
  }

  private shouldSaveStorageDetails() {
    return this.storageRows.length > 0;
  }

  private shouldSavePosmInstallationDetails() {
    return this.isPosm() && this.posmInstallationRows.length > 0;
  }

  private hasText(value: unknown) {
    return typeof value === 'string' && value.trim().length > 0;
  }

  private hasPositiveNumber(value: unknown) {
    return Number(value || 0) > 0;
  }

  private hasMeaningfulQuantity(value: unknown, defaultValue: number) {
    const numericValue = Number(value ?? defaultValue);
    return numericValue !== defaultValue && numericValue > 0;
  }

  private syncPosmFromGeneral() {
    if (!this.isPosm()) return;

    const subject = (this.quotationForm.get('subject')?.value || '').trim();
    this.syncPosmControl('projectName', subject, 'lastSyncedPosmProjectName');

    const cityId = this.quotationForm.get('cityId')?.value;
    const city = cityId ? this.cityById(cityId) : null;
    const location = city ? this.cityLabel(city).trim() : '';
    const eventLocationControl = this.posmForm.get('eventLocation');
    const currentLocation = (eventLocationControl?.value || '').trim();
    const shouldSyncLocation = !currentLocation || currentLocation === this.lastSyncedPosmEventLocation;
    if (!shouldSyncLocation) return;

    this.posmForm.patchValue({
      eventCountryId: city?.country?.id ?? null,
      eventCityId: city?.id ?? null,
      eventLocation: location
    }, { emitEvent: false });
    this.lastSyncedPosmEventLocation = location;
  }

  private syncPosmControl(
    controlName: 'projectName' | 'eventLocation',
    nextValue: string,
    syncKey: 'lastSyncedPosmProjectName' | 'lastSyncedPosmEventLocation'
  ) {
    if (!nextValue) return;
    const control = this.posmForm.get(controlName);
    if (!control) return;

    const currentValue = (control.value || '').trim();
    const shouldSync = !currentValue || currentValue === this[syncKey];
    if (!shouldSync) return;

    control.patchValue(nextValue, { emitEvent: false });
    this[syncKey] = nextValue;
  }

  private sourceKey(quote: CrmQuotation) {
    if (quote.clientId) return `CLIENT:${quote.clientId}`;
    if (quote.prospectId) return `PROSPECT:${quote.prospectId}`;
    if (quote.leadId) return `LEAD:${quote.leadId}`;
    return null;
  }

  private sourceKeyFromQueryParams() {
    const type = String(this.route.snapshot.queryParamMap.get('sourceType') || '').toUpperCase();
    const id = Number(this.route.snapshot.queryParamMap.get('sourceId'));
    if (!['CLIENT', 'PROSPECT', 'LEAD'].includes(type) || !id) {
      return null;
    }
    return `${type}:${id}`;
  }

  private applyPendingSourceKey() {
    if (!this.pendingSourceKey || this.quotationId()) return;
    const hasSource = this.sourceOptions().some(option => option.value === this.pendingSourceKey);
    if (!hasSource) return;
    this.quotationForm.patchValue({ sourceKey: this.pendingSourceKey });
    this.pendingSourceKey = null;
  }

  private clientLabel(record: CrmClient) {
    return [record.clientNumber, record.companyName].filter(Boolean).join(' - ') || `Client #${record.id}`;
  }

  private leadLabel(record: CrmLead) {
    return [record.leadNumber, record.companyName].filter(Boolean).join(' - ') || `Lead #${record.id}`;
  }

  private loadRateCardOptions() {
    const vehicleTypes = this.sessionStorageService.getVehicleTypes() ?? [];
    const uoms = this.sessionStorageService.getUOMList() ?? [];
    this.vehicleTypes.set(vehicleTypes);
    this.vehicleConfigs.set(vehicleTypes.flatMap(type => type.vehicleConfigs || []));
    this.vehicleCapacities.set(vehicleTypes.flatMap(type => type.vehicleCapacities || []));
    this.countries.set(this.sessionStorageService.getCountryList() ?? []);
    this.uomOptions.set(this.toUomOptions(uoms));
  }

  private toUomOptions(uoms: UOM[]) {
    const options = uoms
      .map(uom => {
        const value = uom.shortName || uom.name || '';
        const label = [uom.name, uom.shortName && uom.shortName !== uom.name ? uom.shortName : null].filter(Boolean).join(' - ');
        return value ? { label: label || value, value } : null;
      })
      .filter((option): option is { label: string; value: string } => !!option);
    return options.length ? options : ['EA', 'SQM', 'PALLET', 'TRIP'].map(value => ({ label: value, value }));
  }

  private patchLogisticsDetails(details: any) {
    const rateCards = details.deliveryRateCards?.length
      ? details.deliveryRateCards
      : details.deliveryRateCard ? [details.deliveryRateCard] : [];
    this.logisticsForm.patchValue({
      id: details.id ?? null,
      duration: details.duration ?? '',
      workingHours: details.workingHours ?? '',
      scope: details.scope ?? '',
      quantity: details.quantity ?? 1,
      exclusions: details.exclusions ?? '',
      serviceScope: details.serviceScope ?? '',
      shipmentMode: details.shipmentMode ?? '',
      importExportType: details.importExportType ?? '',
      blAwbNumber: details.blAwbNumber ?? '',
      hblHawbNumber: details.hblHawbNumber ?? '',
      vesselOrFlight: details.vesselOrFlight ?? '',
      carrier: details.carrier ?? '',
      originPort: details.originPort ?? '',
      destinationPort: details.destinationPort ?? '',
      etd: details.etd ? new Date(details.etd) : null,
      eta: details.eta ? new Date(details.eta) : null,
      deliveryOrderNumber: details.deliveryOrderNumber ?? '',
      bayanNumber: details.bayanNumber ?? '',
      undertakingNumber: details.undertakingNumber ?? '',
      cargoDescription: details.cargoDescription ?? '',
      deliveryAddress: details.deliveryAddress ?? '',
      clearanceRequired: details.clearanceRequired ?? true,
      transportRequired: details.transportRequired ?? true,
      containerTrackingRequired: details.containerTrackingRequired ?? false
    });
    this.logisticsRateCards.clear();
    if (rateCards.length) {
      rateCards.forEach((rateCard: any) => this.addLogisticsRateCard(rateCard));
    }
  }

  private buildLogisticsDetails(): any {
    const values = this.logisticsRateCards.value as any[];
    const rateCards = values.map(value => this.buildDeliveryRateCard(value));
    const firstRateCard = rateCards[0];
    return {
      ...this.logisticsForm.value,
      deliveryRateCard: firstRateCard,
      deliveryRateCards: rateCards
    };
  }

  private buildPosmInstallationDetails(): any {
    return {
      rows: (this.posmInstallationRows.value as any[]).map((row, index) => ({
        ...row,
        lineNumber: row.lineNumber || index + 1
      }))
    };
  }

  private buildStorageDetails(): any {
    return {
      rows: (this.storageRows.value as any[]).map((row, index) => ({
        ...row,
        lineNumber: row.lineNumber || index + 1,
        amount: this.storageAmount(row)
      }))
    };
  }

  private buildDeliveryRateCard(value: any): DeliveryRateCard {
    const sourceCity = this.cityById(value.sourceCityId);
    const destinationCity = this.cityById(value.destinationCityId);
    const sourceCountry = this.countryById(value.sourceCountryId);
    const destinationCountry = this.countryById(value.destinationCountryId);
    return {
      rateId: value.rateId ?? undefined,
      sourceCountryId: value.sourceCountryId,
      sourceCountry: sourceCountry?.name,
      sourceCityId: value.sourceCityId,
      sourceCity: sourceCity?.cityName,
      destinationCountryId: value.destinationCountryId,
      destinationCountry: destinationCountry?.name,
      destinationCityId: value.destinationCityId,
      destinationCity: destinationCity?.cityName,
      vehicleTypeId: value.vehicleTypeId,
      vehicleConfigId: value.vehicleConfigId,
      vehicleCapacityId: value.vehicleCapacityId,
      distance: value.distance,
      amount: value.amount,
      goodsInsurance: !!value.goodsInsurance,
      portPermit: !!value.portPermit,
      waitingThreshold: value.waitingThreshold,
      waitingCharge: value.waitingCharge,
      rateType: value.rateType,
      unitBasis: value.unitBasis,
      transitTime: value.transitTime,
      buyingCompanyId: null as any,
      sellingCompanyId: this.toolbarService.customer()?.company?.id as any,
      contractRevisionId: null as any
    };
  }

  sourceCitiesFor(rateCard: FormGroup) {
    return this.citiesForCountry(rateCard.get('sourceCountryId')?.value);
  }

  destinationCitiesFor(rateCard: FormGroup) {
    return this.citiesForCountry(rateCard.get('destinationCountryId')?.value);
  }

  vehicleConfigsFor(rateCard: FormGroup) {
    return this.vehicleConfigs().filter(config => config.typeId === rateCard.get('vehicleTypeId')?.value);
  }

  vehicleCapacitiesFor(rateCard: FormGroup) {
    return this.vehicleCapacities().filter(capacity => capacity.typeId === rateCard.get('vehicleTypeId')?.value);
  }

  private citiesForCountry(countryId?: number | null) {
    if (!countryId) return [];
    return this.cities().filter(city => city.country?.id === countryId);
  }

  private loadCitiesForCountry(countryId?: number | null, afterLoad?: () => void) {
    if (!countryId) {
      afterLoad?.();
      return;
    }
    if (this.loadedCityCountryIds.has(countryId)) {
      afterLoad?.();
      return;
    }
    if (this.loadingCityCountryIds.has(countryId)) return;

    this.loadingCityCountryIds.add(countryId);
    this.masterDataService.getCities('en-US', countryId).pipe(
      finalize(() => this.loadingCityCountryIds.delete(countryId))
    ).subscribe({
      next: cities => {
        const existingById = new Map(this.cities().map(city => [city.id, city]));
        (cities ?? []).forEach(city => existingById.set(city.id, city));
        this.cities.set(Array.from(existingById.values()).sort((a, b) => (a.cityName || '').localeCompare(b.cityName || '')));
        this.loadedCityCountryIds.add(countryId);
        afterLoad?.();
      },
      error: () => {
        afterLoad?.();
      }
    });
  }

  private loadCitiesForRateCards() {
    const countryIds = new Set<number>();
    this.logisticsRateCardControls.forEach(rateCard => {
      const sourceCountryId = rateCard.get('sourceCountryId')?.value;
      const destinationCountryId = rateCard.get('destinationCountryId')?.value;
      if (sourceCountryId) countryIds.add(sourceCountryId);
      if (destinationCountryId) countryIds.add(destinationCountryId);
    });
    countryIds.forEach(countryId => this.loadCitiesForCountry(countryId));
  }

  private cityBelongsToCountry(cityId?: number | null, countryId?: number | null) {
    if (!cityId || !countryId) return false;
    return this.cityById(cityId)?.country?.id === countryId;
  }

  private cityById(cityId?: number | null) {
    return this.cities().find(city => city.id === cityId);
  }

  private updateRateCardDistance(index: number) {
    const rateCard = this.logisticsRateCards.at(index);
    const sourceCity = this.cityById(rateCard?.get('sourceCityId')?.value);
    const destinationCity = this.cityById(rateCard?.get('destinationCityId')?.value);
    const source = sourceCity?.coordinates;
    const destination = destinationCity?.coordinates;
    if (!rateCard || source?.latitude == null || source?.longitude == null
      || destination?.latitude == null || destination?.longitude == null) {
      rateCard?.patchValue({ distance: null }, { emitEvent: false });
      return;
    }
    const latitudeDelta = this.degreesToRadians(destination.latitude - source.latitude);
    const longitudeDelta = this.degreesToRadians(destination.longitude - source.longitude);
    const haversine = Math.sin(latitudeDelta / 2) ** 2
      + Math.cos(this.degreesToRadians(source.latitude)) * Math.cos(this.degreesToRadians(destination.latitude))
      * Math.sin(longitudeDelta / 2) ** 2;
    const distance = 2 * 6371.0088 * Math.asin(Math.sqrt(haversine));
    rateCard.patchValue({ distance: this.round(distance) }, { emitEvent: false });
  }

  private degreesToRadians(value: number) {
    return value * Math.PI / 180;
  }

  private countryById(countryId?: number | null) {
    return this.countries().find(country => country.id === countryId);
  }

  private syncGeneralCountryFromCity() {
    const city = this.cityById(this.quotationForm.get('cityId')?.value);
    const countryId = city?.country?.id ?? null;
    this.quotationForm.patchValue({ countryId }, { emitEvent: false });
  }

  private syncPosmEventCityFromLocation() {
    const eventLocation = (this.posmForm.get('eventLocation')?.value || '').trim().toLowerCase();
    if (!eventLocation || this.posmForm.get('eventCityId')?.value) return;
    const city = this.cities().find(candidate => {
      const fullLabel = this.cityLabel(candidate).trim().toLowerCase();
      const cityName = (candidate.cityName || '').trim().toLowerCase();
      return eventLocation === fullLabel || eventLocation === cityName || eventLocation.startsWith(`${cityName},`);
    });
    if (!city) return;
    this.posmForm.patchValue({
      eventCountryId: city.country?.id ?? null,
      eventCityId: city.id
    }, { emitEvent: false });
  }

  private applyContact(customerId: number | null) {
    const customer = this.customerOptions().find(item => item.id === customerId);
    if (!customer) return;
    this.selectContactCustomer(customer);
  }

  private applyTermsTemplateForJobType(jobType?: CrmQuotationJobType | null) {
    if (!jobType) return;
    const template = this.termsTemplates()
      .filter(item => item.active !== false && item.jobType === jobType)
      .sort((a, b) => Number(b.id || 0) - Number(a.id || 0))[0];
    if (!template) return;

    const descriptionControl = this.quotationForm.get('description');
    const termsControl = this.quotationForm.get('termsHtml');
    const currentDescription = descriptionControl?.value || '';
    const currentTerms = termsControl?.value || '';
    const nextDescription = template.description || '';
    const nextTerms = template.termsHtml || '';

    if (nextDescription && (!currentDescription.trim() || currentDescription === this.lastAppliedTemplateDescription)) {
      descriptionControl?.patchValue(nextDescription, { emitEvent: false });
      this.lastAppliedTemplateDescription = nextDescription;
    }
    if (nextTerms && (!currentTerms.trim() || currentTerms === this.lastAppliedTemplateTermsHtml)) {
      termsControl?.patchValue(nextTerms, { emitEvent: false });
      this.lastAppliedTemplateTermsHtml = nextTerms;
    }
  }

  private resolveContactFromSource(record: any) {
    const contact = this.sourceContact(record);
    const linkedCustomerId = Number(record.linkedCustomerId || 0);
    const localMatch = this.findMatchingCustomer(contact, linkedCustomerId);
    if (localMatch) {
      this.selectContactCustomer(localMatch);
      return;
    }

    if (linkedCustomerId) {
      this.contactResolving.set(true);
      this.customerService.getCustomerById(linkedCustomerId)
        .pipe(finalize(() => this.contactResolving.set(false)))
        .subscribe(response => {
          if (response.success && response.data) {
            this.selectContactCustomer(this.upsertCustomerOption(response.data));
            return;
          }
          this.lookupOrCreateContact(contact);
        });
      return;
    }

    this.lookupOrCreateContact(contact);
  }

  private lookupOrCreateContact(contact: { name: string; email: string; phone: string; countryId: number | null }) {
    if (!contact.name || (!contact.email && !contact.phone)) {
      return;
    }

    const lookup = contact.email
      ? this.customerService.getCustomerByEmailAddress(contact.email)
      : this.customerService.getCustomerByMobileNumber(contact.phone);

    this.contactResolving.set(true);
    lookup.pipe(finalize(() => this.contactResolving.set(false))).subscribe(response => {
      if (response.success && response.data) {
        this.selectContactCustomer(this.upsertCustomerOption(response.data));
        return;
      }
      this.createSourceContact(contact);
    });
  }

  private createSourceContact(contact: { name: string; email: string; phone: string; countryId: number | null }) {
    const currentUser = this.toolbarService.customer();
    const customer = {
      name: contact.name,
      emailAddress: contact.email,
      phoneNumber: contact.phone,
      countryId: contact.countryId || currentUser?.company?.countryId,
      company: currentUser?.company,
      corporateRole: 'CLIENT_CONTACT',
      defaultLanguage: 'en',
      isPhoneVerified: false,
      rating: 0
    } as any;

    this.contactResolving.set(true);
    this.customerService.createCustomer(customer)
      .pipe(finalize(() => this.contactResolving.set(false)))
      .subscribe(response => {
        if (response.success && response.data) {
          this.selectContactCustomer(this.upsertCustomerOption(response.data));
          this.messageService.add({ severity: 'success', summary: 'Contact Registered', detail: 'The source contact was added as a customer contact.' });
          return;
        }
        this.retrySourceContactLookup(contact, response.message);
      });
  }

  private retrySourceContactLookup(contact: { name: string; email: string; phone: string; countryId: number | null }, errorMessage?: string) {
    const lookup = contact.email
      ? this.customerService.getCustomerByEmailAddress(contact.email)
      : contact.phone ? this.customerService.getCustomerByMobileNumber(contact.phone) : null;
    if (!lookup) {
      this.messageService.add({ severity: 'warn', summary: 'Contact', detail: errorMessage || 'Unable to register the source contact automatically.' });
      return;
    }
    this.contactResolving.set(true);
    lookup.pipe(finalize(() => this.contactResolving.set(false))).subscribe(response => {
      if (response.success && response.data) {
        this.selectContactCustomer(this.upsertCustomerOption(response.data));
        return;
      }
      this.messageService.add({ severity: 'warn', summary: 'Contact', detail: errorMessage || 'Unable to register the source contact automatically.' });
    });
  }

  private sourceContact(record: any) {
    return {
      name: (record.notifyingPersonName || record.pocName || '').trim(),
      email: (record.primaryEmail || record.pocEmail || '').trim(),
      phone: (record.phoneNumber || record.pocPhoneNumber || record.mobileNumber || '').trim(),
      countryId: record.countryId || this.quotationForm.get('countryId')?.value || this.toolbarService.customer()?.company?.countryId || null
    };
  }

  private findMatchingCustomer(contact: { name: string; email: string; phone: string }, linkedCustomerId?: number) {
    const name = contact.name.toLowerCase();
    const email = contact.email.toLowerCase();
    const phone = this.normalizedPhone(contact.phone);
    return this.customerOptions().find(customer =>
      (!!linkedCustomerId && customer.id === linkedCustomerId) ||
      (!!email && (customer.emailAddress || '').trim().toLowerCase() === email) ||
      (!!phone && this.normalizedPhone(customer.phoneNumber) === phone) ||
      (!!name && (customer.name || '').trim().toLowerCase() === name)
    ) || null;
  }

  private selectContactCustomer(customer: Customer) {
    this.quotationForm.patchValue({
      customerId: customer.id,
      pocName: customer.name || '',
      pocEmail: customer.emailAddress || '',
      pocPhoneNumber: customer.phoneNumber || '',
      pocMobileNumber: ''
    }, { emitEvent: false });
  }

  private upsertCustomerOption(customer: Customer) {
    const customers = this.customerOptions().filter(item => item.id !== customer.id);
    this.customerOptions.set([...customers, customer].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    return customer;
  }

  private normalizedPhone(phone?: string | null) {
    return (phone || '').replace(/[^0-9]/g, '');
  }

  private mapJobTypeToType(jobType?: CrmQuotationJobType | null): CrmQuotationType {
    switch (jobType) {
      case CrmQuotationJobType.POSM_INSTALLATION:
      case CrmQuotationJobType.POSM_REFURBISHMENT:
        return CrmQuotationType.POSM;
      case CrmQuotationJobType.STORAGE:
        return CrmQuotationType.STORAGE;
      case CrmQuotationJobType.TASK:
        return CrmQuotationType.TASK;
      default:
        return CrmQuotationType.LOGISTICS;
    }
  }

  private setBreadcrumb(label: string) {
    this.breadcrumbItems.set([
      { label: 'CRM', routerLink: '/crm/quotations' },
      { label: 'Quotations', routerLink: '/crm/quotations' },
      { label }
    ]);
  }
}
